import React, { useState, useEffect } from "react";
import UserNavMenu from "@/components/user/UserNavMenu";
import UserHeader from "@/components/user/UserHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Home, BarChartHorizontal, PieChart, LineChart, BarChart4, ExternalLink, FolderKanban, Gavel } from "lucide-react";
import Footer from "@/components/layout/Footer";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  BarElement,
  Title, 
  Tooltip, 
  Legend, 
  ArcElement
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import LoginModal from "@/components/auth/LoginModal";

// Registrar os componentes do Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

// Meses para os eixos X dos gráficos
const meses = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

const Dashboard: React.FC = () => {
  // Navegação
  const navigate = useNavigate();
  const location = useLocation();
  
  // Estado para controlar o pop-up de projetos
  const [projetosDialogOpen, setProjetosDialogOpen] = useState(false);

  // Estado para controlar o modal de login
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  
  // Função para verificar se a rota atual corresponde a um determinado padrão
  const isActivePath = (path: string): boolean => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    // Para projetos, verificamos várias rotas possíveis
    if (path === 'projects') {
      return ['/my-projects', '/project/status', '/project/region'].some(
        p => location.pathname.startsWith(p)
      );
    }
    return location.pathname === path;
  };
  
  // Função para navegar para a rota desejada
  const handleNavigation = (path: string) => {
    navigate(path);
  };

  // Verificar se o usuário está logado
  const isUserLoggedIn = (): boolean => {
    return localStorage.getItem('isLoggedIn') === 'true';
  };

  // Função para lidar com o clique no botão Projetos
  const handleProjetosClick = () => {
    navigate('/all-projects');
  };

  // Função executada após login bem-sucedido
  const handleLoginSuccess = () => {
    // Não precisamos fazer nada aqui, pois o LoginModal já redireciona
    console.log("Login realizado com sucesso");
  };

  // Estados para os filtros
  const [ano, setAno] = useState<string>("Todos");
  const [mes, setMes] = useState<string>("Todos");
  const [statusProjeto, setStatusProjeto] = useState<string>("Todos");
  const [dataInicio, setDataInicio] = useState<string>("Todos");
  const [dataFim, setDataFim] = useState<string>("Todos");
  const [descricao, setDescricao] = useState<string>("Todos");
  const [realizadoPor, setRealizadoPor] = useState<string>("Todos");
  const [setor, setSetor] = useState<string>("Todos");
  const [status, setStatus] = useState<string>("Todos");
  const [tipoServico, setTipoServico] = useState<string>("Todos");
  const [tipoProjeto, setTipoProjeto] = useState<string>("Todos");
  const [ods, setOds] = useState<string>("Todos");
  
  // Anos disponíveis para o filtro de Ano
  const anosDisponiveis = ["2025", "2024", "2023", "2022", "2021", "2020"];
  
  // Estados para o filtro de região
  const [tipoRegiao, setTipoRegiao] = useState<string>("Todos");
  const [bairro, setBairro] = useState<string>("Todos");
  const [cidade, setCidade] = useState<string>("Todos");
  const [estado, setEstado] = useState<string>("Todos");
  const [pais, setPais] = useState<string>("Todos");
  
  // Dados simulados para os filtros de região
  const bairros = ["Centro", "Botafogo", "Copacabana", "Ipanema", "Leblon"];
  const cidades = ["Rio de Janeiro", "São Paulo", "Belo Horizonte", "Brasília", "Curitiba"];
  const estados = ["RJ", "SP", "MG", "DF", "PR", "SC", "RS"];
  const paises = ["Brasil", "Argentina", "Chile", "Colômbia", "Peru"];

  // Função para lidar com a mudança do tipo de projeto
  const handleTipoProjetoChange = (value: string) => {
    console.log("Tipo de projeto selecionado:", value);
    setTipoProjeto(value);
  };

  // Efeito para registrar mudanças no tipo de projeto
  useEffect(() => {
    console.log("Tipo de projeto atual:", tipoProjeto);
  }, [tipoProjeto]);

  // Dados para os gráficos e métricas
  const metricas = {
    totalProjetos: 15,
    projetosEmAtraso: 12,
    valorInicial: 8.53,
    valorOrcado: 7.81,
    valorNegociado: 6.80,
    desconto: 1.73,
    descontoPorcentagem: 78.72
  };

  // Dados para o gráfico de projetos por mês
  const dadosProjetosPorMes = {
    labels: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'],
    datasets: [
      {
        label: 'Projetos por Mês',
        data: [12, 19, 3, 5, 2, 3, 9, 13, 5, 12, 7, 4],
        borderColor: '#0e7cb4',
        backgroundColor: 'rgba(14, 124, 180, 0.5)',
        tension: 0.4,
      },
    ],
  };

  // Dados para o gráfico de projetos por região
  const dadosProjetosPorRegiao = {
    labels: ['Norte', 'Nordeste', 'Centro-Oeste', 'Sudeste', 'Sul'],
    datasets: [
      {
        label: 'Projetos por Região',
        data: [14, 28, 16, 42, 23],
        backgroundColor: [
          'rgba(14, 124, 180, 0.7)', // Azul principal
          'rgba(24, 144, 200, 0.7)', // Azul mais claro
          'rgba(46, 204, 113, 0.7)', // Verde principal
          'rgba(38, 166, 91, 0.7)',  // Verde mais escuro
          'rgba(82, 175, 216, 0.7)'  // Azul claro
        ],
        borderColor: [
          '#0e7cb4', // Azul principal
          '#1890c8', // Azul mais claro
          '#2ecc71', // Verde principal
          '#26a65b', // Verde mais escuro
          '#52afd8'  // Azul claro
        ],
        borderWidth: 1
      },
    ],
  };

  // Dados para o gráfico de donut de tipos de serviço
  const dadosTipoServico = {
    labels: ['Consultoria', 'Compliance'],
    datasets: [
      {
        data: [65, 35],
        backgroundColor: ['#0e7cb4', '#27ae60'],
        borderColor: ['#0e7cb4', '#27ae60'],
      },
    ],
  };

  // Dados para o gráfico de status do projeto
  const dadosStatusProjeto = {
    labels: ['Planejamento', 'Em implementação', 'Finalizado', 'Crédito para Venda', 'Crédito aposentado'],
    datasets: [
      {
        data: [25, 40, 15, 12, 8],
        backgroundColor: [
          '#0e7cb4', // Azul escuro para Planejamento
          '#2ecc71', // Verde para Em implementação
          '#52afd8', // Azul claro para Finalizado
          '#1abc9c', // Verde água para Crédito para Venda
          '#3498db'  // Azul médio para Crédito aposentado
        ],
        borderColor: [
          '#0e7cb4',
          '#2ecc71',
          '#52afd8',
          '#1abc9c',
          '#3498db'
        ],
      },
    ],
  };

  // Dados para o gráfico de barras de projetos por colaborador
  const dadosColaboradores = {
    labels: ['João', 'Maria', 'Pedro', 'Ana', 'Carlos'],
    datasets: [
      {
        label: 'Projetos',
        data: [12, 10, 3, 5, 2],
        backgroundColor: [
          '#0e7cb4', // Azul escuro
          '#2ecc71', // Verde
          '#3498db', // Azul médio
          '#1abc9c', // Verde água
          '#52afd8'  // Azul claro
        ],
        barThickness: 20,
        borderRadius: 5,
      },
    ],
  };

  // Dados para o gráfico de tipos de projeto
  const dadosTipoProjeto = {
    labels: ['Energia Renovável', 'Efic. Energética', 'Manejo Resíduos', 'Agric. Sustentável', 'Reflorestamento', 'Conservação'],
    datasets: [
      {
        label: 'Projetos',
        data: [35, 20, 15, 10, 28, 22],
        backgroundColor: [
          '#0e7cb4', // Azul escuro
          '#2ecc71', // Verde
          '#3498db', // Azul médio
          '#1abc9c', // Verde água
          '#52afd8', // Azul claro
          '#26a65b'  // Verde escuro
        ],
        barThickness: 20,
        borderRadius: 5,
      },
    ],
  };

  // Dados para o gráfico de barras horizontais de Projetos por CNPJ
  const dadosCNPJ = {
    labels: ['12.345.678/0001-XX', '98.765.432/0001-XX', '45.678.912/0001-XX', '78.912.345/0001-XX', '32.165.498/0001-XX'],
    datasets: [
      {
        label: 'Projetos',
        data: [10, 5, 8, 12, 6],
        backgroundColor: [
          '#0e7cb4', // Azul escuro
          '#2ecc71', // Verde
          '#3498db', // Azul médio
          '#1abc9c', // Verde água
          '#52afd8'  // Azul claro
        ],
        barThickness: 20,
        borderRadius: 5,
      },
    ],
  };

  // Dados para o gráfico de projetos por CPF
  const dadosCPF = {
    labels: ['123.456.XXX-XX', '789.012.XXX-XX', '345.678.XXX-XX', '901.234.XXX-XX', '567.890.XXX-XX'],
    datasets: [
      {
        label: 'Projetos',
        data: [8, 5, 12, 6, 4],
        backgroundColor: [
          '#0e7cb4', // Azul escuro
          '#2ecc71', // Verde
          '#3498db', // Azul médio
          '#1abc9c', // Verde água
          '#52afd8'  // Azul claro
        ],
        barThickness: 20,
        borderRadius: 5,
      },
    ],
  };

  // Dados para o gráfico de linhas do Pregão Eletrônico
  const dadosPregaoEletronico = {
    labels: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'],
    datasets: [
      {
        label: 'Pregões Realizados',
        data: [5, 8, 6, 10, 7, 12, 9, 11, 8, 13, 10, 14],
        borderColor: '#0e7cb4',
        backgroundColor: 'rgba(14, 124, 180, 0.2)',
        tension: 0.4,
      },
    ],
  };

  // Opções comuns para os gráficos
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#666',
          font: {
            size: 10,
          },
        },
        border: {
          display: false,
        },
      },
      y: {
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
        ticks: {
          color: '#666',
          font: {
            size: 10,
          },
        },
        border: {
          display: false,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(14, 124, 180, 0.8)',
        titleFont: {
          size: 12,
        },
        bodyFont: {
          size: 11,
        },
        padding: 8,
        cornerRadius: 4,
      },
    },
  };

  // Opções para o gráfico de donut
  const donutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '70%',
    plugins: {
      legend: {
        display: false,
      },
    },
  };

  // Opções para o gráfico de barras horizontais
  const barOptions = {
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
      },
      y: {
        grid: {
          display: false,
        },
      },
    },
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <UserHeader />
      
      {/* Menu horizontal superior */}
      <div className="bg-white border-b border-gray-200 px-6">
        <div className="flex space-x-6 overflow-x-auto py-3">
          <button 
            className={`${isActivePath('/planos') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/planos')}
          >
            Planos
          </button>
          <button 
            className={`${isActivePath('/dashboard') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/dashboard')}
          >
            Dashboard
          </button>
          <button 
            className={`${isActivePath('projects') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={handleProjetosClick}
          >
            Projetos
          </button>
          <button 
            className={`${isActivePath('/emission') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/emission')}
          >
            Emissão
          </button>
          <button 
            className={`${isActivePath('/traceability') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/traceability')}
          >
            Rastreabilidade
          </button>
          <button 
            className={`${isActivePath('/certificates') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/certificates')}
          >
            Certificados
          </button>
          <button
            className={`${isActivePath('/pregao-dashboard') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/pregao-dashboard')}
          >
            Bolsa de Crédito de Carbono Voluntário
          </button>
          <button 
            className={`${isActivePath('/compliance') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/compliance')}
          >
            Conformidade
          </button>
          <button 
            className={`${isActivePath('/retirement') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/retirement')}
          >
            Aposentadoria
          </button>
          <button 
            className={`${isActivePath('/relatorios') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/relatorios')}
          >
            Relatórios
          </button>
          <button 
            className={`${isActivePath('/educational') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/educational')}
          >
            Espaço Educativo
          </button>
        </div>
        </div>
        
      {/* Pop-up de Acesso para Projetos */}
      <Dialog open={projetosDialogOpen} onOpenChange={setProjetosDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center text-xl">Acesso a Projetos</DialogTitle>
            <DialogDescription className="text-center">
              Selecione o tipo de visualização de projetos que deseja acessar.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            <Card className="border border-gray-200 hover:border-[#0e7cb4] hover:shadow-md cursor-pointer transition-all" onClick={() => {
              setProjetosDialogOpen(false);
              handleNavigation('/my-projects');
            }}>
              <CardContent className="p-6 flex flex-col items-center justify-center">
                <div className="bg-[#0e7cb4]/10 p-3 rounded-full mb-3">
                  <Home className="h-6 w-6 text-[#0e7cb4]" />
                </div>
                <h3 className="text-lg font-medium mb-1">Meus Projetos</h3>
                <p className="text-sm text-gray-500 text-center">Visualize todos os seus projetos cadastrados</p>
            </CardContent>
          </Card>

            <Card className="border border-gray-200 hover:border-[#0e7cb4] hover:shadow-md cursor-pointer transition-all" onClick={() => {
              setProjetosDialogOpen(false);
              handleNavigation('/project/status');
            }}>
              <CardContent className="p-6 flex flex-col items-center justify-center">
                <div className="bg-[#0e7cb4]/10 p-3 rounded-full mb-3">
                  <BarChart4 className="h-6 w-6 text-[#0e7cb4]" />
                </div>
                <h3 className="text-lg font-medium mb-1">Status dos Projetos</h3>
                <p className="text-sm text-gray-500 text-center">Acompanhe o status de todos os projetos</p>
            </CardContent>
          </Card>

            <Card className="border border-gray-200 hover:border-[#0e7cb4] hover:shadow-md cursor-pointer transition-all" onClick={() => {
              setProjetosDialogOpen(false);
              handleNavigation('/project/region');
            }}>
              <CardContent className="p-6 flex flex-col items-center justify-center">
                <div className="bg-[#0e7cb4]/10 p-3 rounded-full mb-3">
                  <PieChart className="h-6 w-6 text-[#0e7cb4]" />
                </div>
                <h3 className="text-lg font-medium mb-1">Projetos por Região</h3>
                <p className="text-sm text-gray-500 text-center">Explore projetos por localização geográfica</p>
            </CardContent>
          </Card>

            <Card className="border border-gray-200 hover:border-[#0e7cb4] hover:shadow-md cursor-pointer transition-all" onClick={() => {
              window.open('https://banclima.com/projetos', '_blank');
              setProjetosDialogOpen(false);
            }}>
              <CardContent className="p-6 flex flex-col items-center justify-center">
                <div className="bg-[#0e7cb4]/10 p-3 rounded-full mb-3">
                  <ExternalLink className="h-6 w-6 text-[#0e7cb4]" />
                </div>
                <h3 className="text-lg font-medium mb-1">Portal de Projetos</h3>
                <p className="text-sm text-gray-500 text-center">Acesse o portal público de projetos do Banclima</p>
            </CardContent>
          </Card>
        </div>
          <DialogFooter className="sm:justify-center">
            <Button 
              variant="outline" 
              onClick={() => setProjetosDialogOpen(false)}
            >
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Modal de Login */}
      <LoginModal 
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onSuccess={handleLoginSuccess}
        redirectTo="/my-projects"
      />
      
      <div className="flex flex-grow">
        {/* Painel lateral de filtros */}
        <div className="w-[170px] bg-gray-100 p-4 space-y-4">
          <div className="mb-6">
            <p className="text-sm text-gray-700 mb-2">Tipo de Projeto</p>
            <select 
              className="w-full px-3 py-2 rounded bg-white border border-gray-300 text-gray-800 text-sm"
              value={tipoProjeto}
              onChange={(e) => {
                console.log("Tipo de projeto selecionado:", e.target.value);
                setTipoProjeto(e.target.value);
              }}
            >
              <option value="Todos">Todos</option>
              <option value="Energia Renovável">Energia Renovável</option>
              <option value="Eficiência Energética">Eficiência Energética</option>
              <option value="Manejo de Resíduos">Manejo de Resíduos</option>
              <option value="Agricultura Sustentável">Agricultura Sustentável</option>
              <option value="Reflorestamento / Restauração">Reflorestamento / Restauração</option>
              <option value="Conservação de Florestas">Conservação de Florestas</option>
              <option value="Outros">Outros</option>
            </select>
          </div>

          <div className="mb-6">
            <p className="text-sm text-gray-700 mb-2">Status do Projeto</p>
            <select 
              className="w-full px-3 py-2 rounded bg-white border border-gray-300 text-gray-800 text-sm"
              value={statusProjeto}
              onChange={(e) => {
                console.log("Status de projeto selecionado:", e.target.value);
                setStatusProjeto(e.target.value);
              }}
            >
              <option value="Todos">Todos</option>
              <option value="Planejamento">Planejamento</option>
              <option value="Em implementação">Em implementação</option>
              <option value="Finalizado">Finalizado</option>
              <option value="Crédito para Venda">Crédito para Venda</option>
              <option value="Crédito aposentado">Crédito aposentado</option>
            </select>
          </div>

          <div className="mb-6">
            <p className="text-sm text-gray-700 mb-2">Região</p>
            <select 
              className="w-full px-3 py-2 rounded bg-white border border-gray-300 text-gray-800 text-sm"
              value={tipoRegiao}
              onChange={(e) => {
                console.log("Região selecionada:", e.target.value);
                setTipoRegiao(e.target.value);
              }}
            >
              <option value="Todos">Todos</option>
              <option value="Norte">Norte</option>
              <option value="Nordeste">Nordeste</option>
              <option value="Sul">Sul</option>
              <option value="Sudeste">Sudeste</option>
              <option value="Centro-Oeste">Centro-Oeste</option>
            </select>
          </div>

          <div className="mb-6">
            <p className="text-sm text-gray-700 mb-2">ODS</p>
            <select 
              className="w-full px-3 py-2 rounded bg-white border border-gray-300 text-gray-800 text-sm"
              value={ods}
              onChange={(e) => {
                console.log("ODS selecionado:", e.target.value);
                setOds(e.target.value);
              }}
            >
              <option value="Todos">Todos</option>
              <option value="ODS 1">ODS 1</option>
              <option value="ODS 2">ODS 2</option>
              <option value="ODS 3">ODS 3</option>
              <option value="ODS 4">ODS 4</option>
              <option value="ODS 5">ODS 5</option>
              <option value="ODS 6">ODS 6</option>
              <option value="ODS 7">ODS 7</option>
              <option value="ODS 8">ODS 8</option>
              <option value="ODS 9">ODS 9</option>
              <option value="ODS 10">ODS 10</option>
              <option value="ODS 11">ODS 11</option>
              <option value="ODS 12">ODS 12</option>
              <option value="ODS 13">ODS 13</option>
              <option value="ODS 14">ODS 14</option>
              <option value="ODS 15">ODS 15</option>
              <option value="ODS 16">ODS 16</option>
              <option value="ODS 17">ODS 17</option>
            </select>
          </div>

          <div className="mb-6">
            <p className="text-sm text-gray-700 mb-2">Ano</p>
            <select 
              className="w-full px-3 py-2 rounded bg-white border border-gray-300 text-gray-800 text-sm"
              value={descricao}
              onChange={(e) => {
                console.log("Ano selecionado:", e.target.value);
                setDescricao(e.target.value);
              }}
            >
              <option value="Todos">Todos</option>
              {anosDisponiveis.map((ano) => (
                <option key={ano} value={ano}>{ano}</option>
              ))}
            </select>
          </div>

          <div className="mb-6">
            <p className="text-sm text-gray-700 mb-2">CNPJ</p>
            <Select value={realizadoPor} onValueChange={setRealizadoPor}>
              <SelectTrigger className="bg-white border-gray-300 text-gray-800">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent className="bg-white border-gray-300 text-gray-800">
                <SelectGroup>
                  <SelectItem value="Todos">Todos</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="mb-6">
            <p className="text-sm text-gray-700 mb-2">CPF</p>
            <Select value={setor} onValueChange={setSetor}>
              <SelectTrigger className="bg-white border-gray-300 text-gray-800">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent className="bg-white border-gray-300 text-gray-800">
                <SelectGroup>
                  <SelectItem value="Todos">Todos</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="mb-6">
            <p className="text-sm text-gray-700 mb-2">Pregões</p>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="bg-white border-gray-300 text-gray-800">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent className="bg-white border-gray-300 text-gray-800">
                <SelectGroup>
                  <SelectItem value="Todos">Todos</SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Conteúdo principal */}
        <div className="flex-1 p-6">
          {/* Cabeçalho com título e ícones */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-3">
              <div className="bg-[#0e7cb4] p-2 rounded-md">
                <svg className="w-6 h-6 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2L2 7l10 5 10-5-10-5z" />
                  <path d="M2 17l10 5 10-5" />
                  <path d="M2 12l10 5 10-5" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-gray-800">OVERVIEW</h1>
            </div>
            <div className="flex items-center gap-4">
              <button className="text-gray-600 hover:text-[#0e7cb4]">
                <BarChart4 className="w-6 h-6" />
              </button>
              <button className="text-gray-600 hover:text-[#0e7cb4]">
                <PieChart className="w-6 h-6" />
              </button>
              <button className="text-gray-600 hover:text-[#0e7cb4]">
                <LineChart className="w-6 h-6" />
              </button>
              <button className="text-gray-600 hover:text-[#0e7cb4]">
                <BarChartHorizontal className="w-6 h-6" />
              </button>
              <button className="text-gray-600 hover:text-[#0e7cb4]">
                <Home className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Três gráficos na linha do meio */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <Card className="border border-gray-200">
              <CardContent className="p-4">
                <div className="text-sm font-medium mb-3 text-gray-700">Projetos por Região</div>
                <div className="h-[200px]">
                  <Bar options={options} data={dadosProjetosPorRegiao} />
                </div>
              </CardContent>
            </Card>

            <Card className="border border-gray-200">
              <CardContent className="p-4">
                <div className="text-sm font-medium mb-3 text-gray-700">Status do Projeto</div>
                <div className="h-[200px] flex justify-center items-center">
                  <Doughnut options={donutOptions} data={dadosStatusProjeto} />
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                  {dadosStatusProjeto.labels.map((label, index) => (
                    <div key={index} className="flex items-center">
                      <div 
                        className="w-3 h-3 rounded-full mr-2" 
                        style={{ backgroundColor: dadosStatusProjeto.datasets[0].backgroundColor[index] }}
                      ></div>
                      <div className="flex justify-between w-full">
                        <span>{label}</span>
                        <span className="font-medium">{dadosStatusProjeto.datasets[0].data[index]}%</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-2 border-t border-gray-100 text-xs text-gray-500 flex justify-between">
                  <div>Total de projetos: {dadosStatusProjeto.datasets[0].data.reduce((a, b) => a + b, 0)}</div>
                  <div className="font-medium text-[#0e7cb4]">
                    <span className="inline-flex items-center">
                      <svg className="w-3 h-3 mr-1" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 19V5M12 5L5 12M12 5L19 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      8% último mês
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-gray-200">
              <CardContent className="p-4">
                <div className="text-sm font-medium mb-3 text-gray-700">Projetos por CPF</div>
                <div className="h-[200px]">
                  <Bar options={options} data={dadosCPF} />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Dois gráficos na linha de baixo */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <Card className="border border-gray-200">
              <CardContent className="p-4">
                <div className="text-sm font-medium mb-3 text-gray-700">Projetos por CNPJ</div>
                <div className="h-[200px]">
                  <Bar options={barOptions} data={dadosCNPJ} />
                </div>
              </CardContent>
            </Card>

            <Card className="border border-gray-200">
              <CardContent className="p-4">
                <div className="text-sm font-medium mb-3 text-gray-700">Pregão Eletrônico por Mês</div>
                <div className="h-[200px]">
                  <Line options={options} data={dadosPregaoEletronico} />
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 gap-4 mb-6">
            <Card className="border border-gray-200">
              <CardContent className="p-4">
                <div className="text-sm font-medium mb-3 text-gray-700">Tipo de Projeto</div>
                <div className="h-[200px]">
                  <Bar options={options} data={dadosTipoProjeto} />
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                  {dadosTipoProjeto.labels.map((label, index) => (
                    <div key={index} className="flex items-center">
                      <div 
                        className="w-3 h-3 rounded-full mr-2" 
                        style={{ backgroundColor: dadosTipoProjeto.datasets[0].backgroundColor[index] }}
                      ></div>
                      <div className="flex justify-between w-full">
                        <span>{label}</span>
                        <span className="font-medium">{dadosTipoProjeto.datasets[0].data[index]}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-2 border-t border-gray-100 text-xs text-gray-500 flex justify-between">
                  <div>Total de projetos: {dadosTipoProjeto.datasets[0].data.reduce((a, b) => a + b, 0)}</div>
                  <div className="font-medium text-[#0e7cb4]">
                    <span className="inline-flex items-center">
                      <svg className="w-3 h-3 mr-1" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 19V5M12 5L5 12M12 5L19 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      15% último mês
                    </span>
                </div>
              </div>
            </CardContent>
          </Card>
          </div>
        </div>
      </div>
      
      {/* Rodapé */}
      <Footer />
    </div>
  );
};

export default Dashboard; 



